import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { selectTranslationState } from '../../../store/slices/languageSlice'
import CustomInput from '../../../components/CustomInput'
import CustomButton from '../../../components/CustomButton'
import SearchableSelect from '../../../components/SearchableSelect'
import { AppDispatch, RootState } from '../../../store/store'
import useCustomStyles from '../../../hooks/useCustomStyles'
import { selectState } from '../../../store/slices/themeSlice'
import { header2Styles } from '../../../assets/styles/header2'
import { ClientType } from '../../../store/slices/clientsSlice'
import {
  assignClientId,
  fetchAssignableClientIds,
  AssignableClientId,
} from '../../../store/actions/clientsActions'

interface AddNewClientModalProps {
  isOpen: boolean
  onConfirm: () => void
  onCancel: () => void
  client: ClientType | null
}

const formatOption = (o: AssignableClientId) =>
  `${o.clientId}  ·  ${o.users_count}/${o.users_limit} used`

const AddClientIDModal: React.FC<AddNewClientModalProps> = ({
  isOpen,
  onConfirm,
  onCancel,
  client,
}) => {
  const modalRef = useRef<HTMLDivElement>(null)
  const { t } = useSelector(selectTranslationState)
  const { theme, textTheme } = useSelector(selectState)
  const { loading } = useSelector((state: RootState) => state.clients_slice)
  const classes = useCustomStyles(header2Styles, theme)

  const dispatch = useDispatch<AppDispatch>()

  const isEdit = Boolean(client?.clientId)

  // Assigning to a client ID — whether this is the client's first one
  // ("Add") or a switch away from their current one ("Edit"), it's the same
  // operation: pick an available bucket for the client's business and link
  // to it. assignPartnerClientIdToUser_v2 handles both the same way,
  // freeing the client's previous bucket's seat when there was one.
  const [options, setOptions] = useState<AssignableClientId[]>([])
  const [loadingOptions, setLoadingOptions] = useState(false)
  const [selectedLabel, setSelectedLabel] = useState('')

  const selectableOptions = useMemo(
    () => options.filter((o) => o.clientId !== client?.clientId),
    [options, client?.clientId],
  )
  const optionByLabel = new Map(selectableOptions.map((o) => [formatOption(o), o]))

  useEffect(() => {
    if (!isOpen || !client?.business_name) return
    setSelectedLabel('')
    setLoadingOptions(true)
    fetchAssignableClientIds(client.business_name)
      .then(setOptions)
      .catch((e) => {
        toast.error(e instanceof Error ? e.message : 'Failed to load available client IDs')
        setOptions([])
      })
      .finally(() => setLoadingOptions(false))
  }, [isOpen, client?.business_name])

  const handleAssign = async () => {
    if (!client) return
    const selected = optionByLabel.get(selectedLabel)
    if (!selected) {
      toast.error('Please select a client ID.')
      return
    }
    try {
      await dispatch(
        assignClientId({ clientDocId: client.id, partnerClientIdId: selected.id }),
      ).unwrap()
      onConfirm()
    } catch (err) {
      const message =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message)
          : 'Failed to assign client ID'
      toast.error(message)
    }
  }

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        onCancel()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick)
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [isOpen, onCancel])

  if (!isOpen) return null

  return (
    <div className={classes.overlay}>
      <div className={classes.modal} ref={modalRef}>
        <h2 className={classes.headerTextLogOutModal}>
          {isEdit ? `EDIT` : `ADD`} CLIENT ID
        </h2>

        <CustomInput
          label={'Name'}
          name="user_name"
          type="text"
          disabled
          value={client?.user_name || ''}
          onChange={() => {}}
        />

        <CustomInput
          label={t[textTheme]?.addClientScreen?.form?.email?.label || 'Email'}
          name="email"
          type="email"
          disabled
          value={client?.email || ''}
          onChange={() => {}}
        />

        {isEdit && (
          <CustomInput
            label={
              t[textTheme]?.addClientScreen?.form?.clientId?.label ||
              'Current Client ID'
            }
            name="currentClientId"
            type="text"
            disabled
            value={client?.clientId || ''}
            onChange={() => {}}
          />
        )}

        <SearchableSelect
          label={isEdit ? 'New Client ID' : 'Client ID'}
          placeholder={
            loadingOptions
              ? 'Loading available client IDs...'
              : 'Select a client ID'
          }
          options={selectableOptions.map(formatOption)}
          value={selectedLabel}
          onChange={setSelectedLabel}
        />
        {!loadingOptions && selectableOptions.length === 0 && (
          <p style={{ marginTop: '4px', marginBottom: '12px', opacity: 0.8 }}>
            No available client IDs for {client?.business_name || 'this business'}.
          </p>
        )}
        {isEdit && (
          <p style={{ marginTop: '4px', marginBottom: '12px', opacity: 0.8 }}>
            Selecting a client ID moves this client to it: their seat on{' '}
            {client?.clientId} is freed, and they are counted against the new
            one instead.
          </p>
        )}

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '20px',
          }}
        >
          <CustomButton
            title={'Save'}
            name="addClientButton"
            handleClick={handleAssign}
            type="button"
            loading={loading}
            disabled={!selectedLabel}
          />
        </div>
      </div>
    </div>
  )
}

export default AddClientIDModal
